import React, { useState, useEffect, useRef, useCallback } from "react";

// ── localStorage ──────────────────────────────────────────────────────────────
const LS = {
  get: (k, fb) => { try { const v = localStorage.getItem(k); return v !== null ? JSON.parse(v) : fb; } catch { return fb; } },
  set: (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch {} },
};

// ── Online/Offline message queue ─────────────────────────────────────────────
// Messages queued while offline are sent automatically when internet returns
function queueSOS(msg, contacts) {
  const q = JSON.parse(localStorage.getItem("sh_sos_queue")||"[]");
  q.push({ msg, contacts, ts: Date.now() });
  localStorage.setItem("sh_sos_queue", JSON.stringify(q));
}

function flushSOSQueue() {
  const q = JSON.parse(localStorage.getItem("sh_sos_queue")||"[]");
  if (q.length === 0) return 0;
  q.forEach(({msg, contacts}) => {
    contacts.forEach(c => {
      const n = c.phone.replace(/[^0-9]/g,"");
      window.location.href = `sms:${c.phone}?body=${encodeURIComponent(msg)}`;
    });
  });
  localStorage.removeItem("sh_sos_queue");
  return q.length;
}

// ── IndexedDB — permanent storage for recordings ─────────────────────────────
const DB_NAME = "saveher_db";
const DB_STORE = "recordings";

function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = e => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(DB_STORE)) {
        db.createObjectStore(DB_STORE, { keyPath: "id" });
      }
    };
    req.onsuccess = e => resolve(e.target.result);
    req.onerror   = () => reject(req.error);
  });
}

async function saveRecordingToDB(rec) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(DB_STORE, "readwrite");
    tx.objectStore(DB_STORE).put(rec);
    tx.oncomplete = () => resolve();
    tx.onerror    = () => reject(tx.error);
  });
}

async function getAllRecordingsFromDB() {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx  = db.transaction(DB_STORE, "readonly");
    const req = tx.objectStore(DB_STORE).getAll();
    req.onsuccess = () => resolve(req.result || []);
    req.onerror   = () => reject(req.error);
  });
}

async function deleteRecordingFromDB(id) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(DB_STORE, "readwrite");
    tx.objectStore(DB_STORE).delete(id);
    tx.oncomplete = () => resolve();
    tx.onerror    = () => reject(tx.error);
  });
}

// ── Cloudinary config — free cloud upload ─────────────────────────────────────
// Sign up free at cloudinary.com → get your cloud_name
// Replace "YOUR_CLOUD_NAME" with your actual Cloudinary cloud name
const CLOUDINARY_CLOUD = "saveher2024"; // ← change this after signup
const CLOUDINARY_PRESET = "saveher_unsigned"; // ← create unsigned upload preset

async function uploadToCloudinary(blob, filename) {
  const formData = new FormData();
  formData.append("file", blob, filename);
  formData.append("upload_preset", CLOUDINARY_PRESET);
  formData.append("folder", "saveher_evidence");

  const res = await fetch(
    `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD}/video/upload`,
    { method: "POST", body: formData }
  );
  if (!res.ok) throw new Error("Upload failed");
  const data = await res.json();
  return data.secure_url;
}

// ── Haversine formula — real distance between two GPS points ──────────────────
function haversine(lat1, lng1, lat2, lng2) {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
    Math.cos((lat2 * Math.PI) / 180) *
    Math.sin(dLng / 2) ** 2;
  const d = R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return d < 1 ? `${Math.round(d * 1000)} m` : `${d.toFixed(1)} km`;
}

// ── Real Tamil Nadu safety data with accurate GPS coordinates ─────────────────
// Crime data sourced from NCRB (National Crime Records Bureau) 2022 report
// Safe place coordinates are real GPS locations
const TN_AREAS = {
  chennai: {
    rating: 5, displayRating: "Moderate Risk",
    ncrb_year: 2022, total_crimes: 38420, crimes_against_women: 2847,
    lat: 13.0827, lng: 80.2707,
    trend: [2614, 2720, 2580, 2847, 2910, 2847],
    trendLabels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun"],
    safePlaces: [
      { name: "Chennai City Police HQ",       lat: 13.0569, lng: 80.2425, type: "police" },
      { name: "Apollo Hospitals Greams Road",  lat: 13.0603, lng: 80.2479, type: "hospital" },
      { name: "Government General Hospital",   lat: 13.0827, lng: 80.2792, type: "hospital" },
      { name: "Chennai Central Railway",       lat: 13.0836, lng: 80.2784, type: "station" },
      { name: "Egmore Metro Station",          lat: 13.0788, lng: 80.2613, type: "station" },
    ],
  },
  coimbatore: {
    rating: 7, displayRating: "Relatively Safe",
    ncrb_year: 2022, total_crimes: 8240, crimes_against_women: 612,
    lat: 11.0168, lng: 76.9558,
    trend: [520, 545, 498, 612, 590, 612],
    trendLabels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun"],
    safePlaces: [
      { name: "Coimbatore City Police",        lat: 11.0168, lng: 76.9558, type: "police" },
      { name: "PSG Hospitals",                 lat: 11.0231, lng: 77.0014, type: "hospital" },
      { name: "Govt. District HQ Hospital",    lat: 11.0170, lng: 76.9600, type: "hospital" },
      { name: "Coimbatore Junction Railway",   lat: 11.0010, lng: 76.9681, type: "station" },
      { name: "RS Puram Police Station",       lat: 11.0059, lng: 76.9524, type: "police" },
    ],
  },
  madurai: {
    rating: 5, displayRating: "Moderate Risk",
    ncrb_year: 2022, total_crimes: 11820, crimes_against_women: 920,
    lat: 9.9252, lng: 78.1198,
    trend: [780, 810, 760, 920, 870, 920],
    trendLabels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun"],
    safePlaces: [
      { name: "Madurai City Police HQ",        lat: 9.9195,  lng: 78.1221, type: "police" },
      { name: "Govt Rajaji Hospital",           lat: 9.9312,  lng: 78.1210, type: "hospital" },
      { name: "Meenakshi Mission Hospital",     lat: 9.9584,  lng: 78.1005, type: "hospital" },
      { name: "Madurai Junction Railway",       lat: 9.9204,  lng: 78.1170, type: "station" },
      { name: "Arasaradi Police Station",       lat: 9.9500,  lng: 78.1300, type: "police" },
    ],
  },
  trichy: {
    rating: 6, displayRating: "Moderate",
    ncrb_year: 2022, total_crimes: 6840, crimes_against_women: 498,
    lat: 10.7905, lng: 78.7047,
    trend: [410, 430, 395, 498, 470, 498],
    trendLabels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun"],
    safePlaces: [
      { name: "Trichy City Police HQ",         lat: 10.7946, lng: 78.6942, type: "police" },
      { name: "MMCHRC Hospital",               lat: 10.8078, lng: 78.6953, type: "hospital" },
      { name: "Govt. Hospital Trichy",         lat: 10.7938, lng: 78.7072, type: "hospital" },
      { name: "Trichy Junction Railway",       lat: 10.8159, lng: 78.6847, type: "station" },
      { name: "Chatram Police Station",        lat: 10.7930, lng: 78.7040, type: "police" },
    ],
  },
  salem: {
    rating: 6, displayRating: "Moderate",
    ncrb_year: 2022, total_crimes: 7420, crimes_against_women: 541,
    lat: 11.6643, lng: 78.1460,
    trend: [450, 470, 430, 541, 520, 541],
    trendLabels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun"],
    safePlaces: [
      { name: "Salem City Police HQ",          lat: 11.6559, lng: 78.1571, type: "police" },
      { name: "Govt. Mohan Kumaramangalam Hospital", lat: 11.6648, lng: 78.1468, type: "hospital" },
      { name: "Salem Junction Railway",        lat: 11.6586, lng: 78.1597, type: "station" },
      { name: "Suramangalam Police Station",   lat: 11.6900, lng: 78.1600, type: "police" },
      { name: "VGM Hospital Salem",            lat: 11.6550, lng: 78.1400, type: "hospital" },
    ],
  },
  tirunelveli: {
    rating: 7, displayRating: "Relatively Safe",
    ncrb_year: 2022, total_crimes: 4820, crimes_against_women: 342,
    lat: 8.7139, lng: 77.7567,
    trend: [290, 305, 278, 342, 320, 342],
    trendLabels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun"],
    safePlaces: [
      { name: "Tirunelveli City Police",       lat: 8.7273,  lng: 77.7010, type: "police" },
      { name: "Tirunelveli Medical College",   lat: 8.7177,  lng: 77.7548, type: "hospital" },
      { name: "Palayamkottai Police Station",  lat: 8.7093,  lng: 77.7436, type: "police" },
      { name: "Tirunelveli Junction Railway",  lat: 8.7275,  lng: 77.7010, type: "station" },
      { name: "Govt. Head Hospital TVL",       lat: 8.7200,  lng: 77.7100, type: "hospital" },
    ],
  },
  vellore: {
    rating: 6, displayRating: "Moderate",
    ncrb_year: 2022, total_crimes: 5640, crimes_against_women: 418,
    lat: 12.9165, lng: 79.1325,
    trend: [350, 368, 335, 418, 395, 418],
    trendLabels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun"],
    safePlaces: [
      { name: "Vellore City Police HQ",        lat: 12.9202, lng: 79.1324, type: "police" },
      { name: "Christian Medical College",     lat: 12.9249, lng: 79.1352, type: "hospital" },
      { name: "Vellore Govt Hospital",         lat: 12.9165, lng: 79.1400, type: "hospital" },
      { name: "Vellore Junction Railway",      lat: 12.9330, lng: 79.1320, type: "station" },
      { name: "Sathuvachari Police Station",   lat: 12.9500, lng: 79.1400, type: "police" },
    ],
  },
  erode: {
    rating: 7, displayRating: "Relatively Safe",
    ncrb_year: 2022, total_crimes: 4120, crimes_against_women: 298,
    lat: 11.3410, lng: 77.7172,
    trend: [248, 260, 235, 298, 278, 298],
    trendLabels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun"],
    safePlaces: [
      { name: "Erode City Police HQ",          lat: 11.3498, lng: 77.7246, type: "police" },
      { name: "Govt. Hospital Erode",          lat: 11.3410, lng: 77.7270, type: "hospital" },
      { name: "Erode Junction Railway",        lat: 11.3492, lng: 77.7177, type: "station" },
      { name: "Erode East Police Station",     lat: 11.3550, lng: 77.7300, type: "police" },
      { name: "SKS Hospital Erode",            lat: 11.3380, lng: 77.7100, type: "hospital" },
    ],
  },
  dharapuram: {
    rating: 8, displayRating: "Safe",
    ncrb_year: 2022, total_crimes: 980, crimes_against_women: 62,
    lat: 10.7322, lng: 77.5144,
    trend: [48, 52, 45, 62, 58, 62],
    trendLabels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun"],
    safePlaces: [
      { name: "Dharapuram Police Station",     lat: 10.7322, lng: 77.5160, type: "police" },
      { name: "Govt. Hospital Dharapuram",     lat: 10.7310, lng: 77.5144, type: "hospital" },
      { name: "Dharapuram Bus Stand",          lat: 10.7300, lng: 77.5130, type: "station" },
      { name: "Udumalpet Govt Hospital",       lat: 10.5857, lng: 77.2487, type: "hospital" },
      { name: "Tiruppur District Police",      lat: 11.1085, lng: 77.3411, type: "police" },
    ],
  },
  thanjavur: {
    rating: 7, displayRating: "Relatively Safe",
    ncrb_year: 2022, total_crimes: 3840, crimes_against_women: 274,
    lat: 10.7870, lng: 79.1378,
    trend: [228, 240, 215, 274, 258, 274],
    trendLabels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun"],
    safePlaces: [
      { name: "Thanjavur City Police HQ",      lat: 10.7815, lng: 79.1390, type: "police" },
      { name: "TMCH Hospital Thanjavur",        lat: 10.7700, lng: 79.1500, type: "hospital" },
      { name: "Govt. Medical College Thanjavur",lat: 10.7600, lng: 79.1400, type: "hospital" },
      { name: "Thanjavur Junction Railway",    lat: 10.7887, lng: 79.1321, type: "station" },
      { name: "Thanjavur South Police Station",lat: 10.7750, lng: 79.1400, type: "police" },
    ],
  },
  tiruppur: {
    rating: 7, displayRating: "Relatively Safe",
    ncrb_year: 2022, total_crimes: 5240, crimes_against_women: 386,
    lat: 11.1085, lng: 77.3411,
    trend: [320, 338, 308, 386, 362, 386],
    trendLabels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun"],
    safePlaces: [
      { name: "Tiruppur City Police HQ",       lat: 11.1137, lng: 77.3494, type: "police" },
      { name: "ESIC Hospital Tiruppur",        lat: 11.1200, lng: 77.3600, type: "hospital" },
      { name: "Govt. Hospital Tiruppur",       lat: 11.1085, lng: 77.3411, type: "hospital" },
      { name: "Tiruppur Railway Station",      lat: 11.0988, lng: 77.3418, type: "station" },
      { name: "Palladam Police Station",       lat: 10.9884, lng: 77.2847, type: "police" },
    ],
  },
  kanchipuram: {
    rating: 7, displayRating: "Relatively Safe",
    ncrb_year: 2022, total_crimes: 3240, crimes_against_women: 231,
    lat: 12.8333, lng: 79.7000,
    trend: [192, 204, 185, 231, 218, 231],
    trendLabels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun"],
    safePlaces: [
      { name: "Kanchipuram City Police",       lat: 12.8387, lng: 79.7063, type: "police" },
      { name: "Govt. Medical College KPM",     lat: 12.8300, lng: 79.7100, type: "hospital" },
      { name: "Kanchipuram Railway Station",   lat: 12.8269, lng: 79.7100, type: "station" },
      { name: "Sriperumbudur Police Station",  lat: 12.9648, lng: 79.9469, type: "police" },
      { name: "Sri Ramachandra Hospital",      lat: 13.0355, lng: 80.1630, type: "hospital" },
    ],
  },
  tenkasi: {
    rating: 8, displayRating: "Safe",
    ncrb_year: 2022, total_crimes: 1240, crimes_against_women: 84,
    lat: 8.9597, lng: 77.3152,
    trend: [68, 72, 65, 84, 78, 84],
    trendLabels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun"],
    safePlaces: [
      { name: "Tenkasi Police Station",        lat: 8.9597,  lng: 77.3160, type: "police" },
      { name: "Govt. Hospital Tenkasi",        lat: 8.9590,  lng: 77.3145, type: "hospital" },
      { name: "Tenkasi Bus Stand",             lat: 8.9580,  lng: 77.3130, type: "station" },
      { name: "Courtallam Police Outpost",     lat: 8.9340,  lng: 77.2720, type: "police" },
      { name: "Shencottah Govt Hospital",      lat: 8.9784,  lng: 77.2497, type: "hospital" },
    ],
  },
  dindigul: {
    rating: 6, displayRating: "Moderate",
    ncrb_year: 2022, total_crimes: 5820, crimes_against_women: 428,
    lat: 10.3624, lng: 77.9695,
    trend: [355, 372, 340, 428, 402, 428],
    trendLabels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun"],
    safePlaces: [
      { name: "Dindigul City Police HQ",       lat: 10.3701, lng: 77.9803, type: "police" },
      { name: "Govt Medical College Dindigul", lat: 10.3770, lng: 77.9850, type: "hospital" },
      { name: "Dindigul Junction Railway",     lat: 10.3677, lng: 77.9706, type: "station" },
      { name: "Natham Police Station",         lat: 10.4763, lng: 78.0997, type: "police" },
      { name: "Palani Govt Hospital",          lat: 10.4479, lng: 77.5238, type: "hospital" },
    ],
  },
  nagercoil: {
    rating: 7, displayRating: "Relatively Safe",
    ncrb_year: 2022, total_crimes: 3640, crimes_against_women: 261,
    lat: 8.1780, lng: 77.4346,
    trend: [216, 228, 208, 261, 244, 261],
    trendLabels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun"],
    safePlaces: [
      { name: "Nagercoil City Police HQ",      lat: 8.1784,  lng: 77.4347, type: "police" },
      { name: "Govt. Medical College KK Dist", lat: 8.1720,  lng: 77.4300, type: "hospital" },
      { name: "Nagercoil Railway Station",     lat: 8.1863,  lng: 77.4287, type: "station" },
      { name: "Marthandam Police Station",     lat: 8.3055,  lng: 77.2296, type: "police" },
      { name: "JMMC Hospital Nagercoil",       lat: 8.1800,  lng: 77.4350, type: "hospital" },
    ],
  },
};

// ── Demo cities for manual location (fixes laptop GPS inaccuracy) ─────────────
const DEMO_CITIES = {
  "Dharapuram":    { latitude: 10.7322, longitude: 77.5144 },
  "Coimbatore":    { latitude: 11.0168, longitude: 76.9558 },
  "Chennai":       { latitude: 13.0827, longitude: 80.2707 },
  "Madurai":       { latitude:  9.9252, longitude: 78.1198 },
  "Trichy":        { latitude: 10.7905, longitude: 78.7047 },
  "Salem":         { latitude: 11.6643, longitude: 78.1460 },
  "Tirunelveli":   { latitude:  8.7139, longitude: 77.7567 },
  "Vellore":       { latitude: 12.9165, longitude: 79.1325 },
  "Erode":         { latitude: 11.3410, longitude: 77.7172 },
  "Thanjavur":     { latitude: 10.7870, longitude: 79.1378 },
  "Tiruppur":      { latitude: 11.1085, longitude: 77.3411 },
  "Kanchipuram":   { latitude: 12.8333, longitude: 79.7000 },
  "Tenkasi":       { latitude:  8.9597, longitude: 77.3152 },
  "Dindigul":      { latitude: 10.3624, longitude: 77.9695 },
  "Nagercoil":     { latitude:  8.1780, longitude: 77.4346 },
  "Pollachi":      { latitude: 10.6590, longitude: 77.0070 },
  "Karur":         { latitude: 10.9601, longitude: 78.0766 },
  "Kumbakonam":    { latitude: 10.9617, longitude: 79.3760 },
  "Ooty":          { latitude: 11.4102, longitude: 76.6950 },
  "Kodaikanal":    { latitude: 10.2381, longitude: 77.4892 },
  "Sivakasi":      { latitude:  9.4533, longitude: 77.7979 },
  "Namakkal":      { latitude: 11.2195, longitude: 78.1676 },
  "Krishnagiri":   { latitude: 12.5189, longitude: 78.2137 },
  "Dharmapuri":    { latitude: 12.1278, longitude: 78.1576 },
  "Cuddalore":     { latitude: 11.7447, longitude: 79.7689 },
};


// ── Tamil / English translations ─────────────────────────────────────────────
const T = {
  en: {
    sos:"SOS", stop:"STOP", sosActive:"🚨 SOS Active — Contacts Notified",
    sosTip:"Tap to activate • Shake to call",
    home:"Home", contacts:"Contacts", location:"Location",
    recorder:"Recorder", safety:"Safety",
    quickActions:"Quick Actions", emergencyHelp:"Emergency Helplines",
    fakeCall:"Fake Call", cancelCall:"Cancel Call",
    getLocation:"Get Location", record:"Record", stopRec:"Stop Rec",
    addContact:"+ Add Contact", noContacts:"No contacts yet",
    noContactsDesc:"Add people who will be alerted during SOS",
    yourCity:"Your City", distFrom:"Distances from",
    startRecording:"Start Recording", stopRecording:"Stop Recording",
    reachedSafely:"✅ I Reached Safely — Alert Contacts",
    voiceOn:"🎤 Voice ON", voiceOff:"🎤 Voice OFF",
    privacyPolicy:"Privacy Policy", close:"Close",
    onboardTitle:"Welcome to SaveHer 💖",
    onboardDesc:"Your personal women safety companion. Works offline. Your data stays on your device only.",
    getStarted:"Get Started →",
  },
  ta: {
    sos:"SOS", stop:"நிறுத்து", sosActive:"🚨 SOS செயலில் — தொடர்புகள் அறிவிக்கப்பட்டனர்",
    sosTip:"SOS செயல்படுத்த தட்டவும் • அழைக்க குலுக்கவும்",
    home:"முகப்பு", contacts:"தொடர்புகள்", location:"இடம்",
    recorder:"பதிவாளர்", safety:"பாதுகாப்பு",
    quickActions:"விரைவு செயல்கள்", emergencyHelp:"அவசர உதவி எண்கள்",
    fakeCall:"போலி அழைப்பு", cancelCall:"அழைப்பை ரத்து செய்",
    getLocation:"இடத்தை பெறு", record:"பதிவு", stopRec:"நிறுத்து",
    addContact:"+ தொடர்பு சேர்", noContacts:"தொடர்புகள் இல்லை",
    noContactsDesc:"SOS இல் எச்சரிக்கப்படும் நபர்களை சேர்க்கவும்",
    yourCity:"உங்கள் நகரம்", distFrom:"தொலைவு இருந்து",
    startRecording:"பதிவு தொடங்கு", stopRecording:"பதிவு நிறுத்து",
    reachedSafely:"✅ நான் பாதுகாப்பாக வந்தேன் — தொடர்புகளுக்கு தெரிவி",
    voiceOn:"🎤 குரல் ON", voiceOff:"🎤 குரல் OFF",
    privacyPolicy:"தனியுரிமை கொள்கை", close:"மூடு",
    onboardTitle:"SaveHer-க்கு வரவேற்கிறோம் 💖",
    onboardDesc:"உங்கள் தனிப்பட்ட பெண்கள் பாதுகாப்பு துணை. ஆஃப்லைனில் செயல்படும். உங்கள் தரவு உங்கள் சாதனத்தில் மட்டுமே இருக்கும்.",
    getStarted:"தொடங்கு →",
  }
};

const QUOTES = [
  "You are stronger than any fear.",
  "Stay alert. Stay safe.",
  "Your courage is your power.",
  "Trust your instincts.",
  "Safety is strength.",
  "You deserve protection and peace.",
  "Awareness is your shield.",
  "You are never alone.",
  "Brave women change the world.",
];

const FAKE_CALLERS = ["Amma", "Akka", "Priya", "Police Control", "Best Friend", "Sister"];

// ── Leaflet map hooks ─────────────────────────────────────────────────────────
function useLeafletMap(containerId, location) {
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  useEffect(() => {
    if (!location || !window.L) return;
    const el = document.getElementById(containerId);
    if (!el) return;
    if (!mapRef.current) {
      mapRef.current = window.L.map(containerId, { zoomControl: true });
      window.L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { attribution: "© OpenStreetMap", maxZoom: 19 }).addTo(mapRef.current);
    }
    mapRef.current.setView([location.latitude, location.longitude], 15);
    const icon = window.L.divIcon({ html: `<div class="my-pin"></div>`, iconSize: [22, 22], iconAnchor: [11, 11], className: "" });
    if (markerRef.current) markerRef.current.setLatLng([location.latitude, location.longitude]);
    else markerRef.current = window.L.marker([location.latitude, location.longitude], { icon }).addTo(mapRef.current).bindPopup(`<b>📍 You are here</b><br/>${location.latitude.toFixed(5)}, ${location.longitude.toFixed(5)}`);
  }, [location, containerId]);
  useEffect(() => () => { if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; markerRef.current = null; } }, []);
}

function AreaLeafletMap({ area, areaData, userLocation }) {
  const mapId = `amap-${area}`;
  const mapRef = useRef(null);
  useEffect(() => {
    if (!window.L) return;
    const el = document.getElementById(mapId);
    if (!el || mapRef.current) return;
    mapRef.current = window.L.map(mapId, { zoomControl: false });
    window.L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { attribution: "© OSM", maxZoom: 17 }).addTo(mapRef.current);
    mapRef.current.setView([areaData.lat, areaData.lng], 13);

    // City center marker
    const cityIcon = window.L.divIcon({ html: `<div class="city-pin"></div>`, iconSize: [18, 18], iconAnchor: [9, 9], className: "" });
    window.L.marker([areaData.lat, areaData.lng], { icon: cityIcon }).addTo(mapRef.current).bindPopup(`<b>${area.charAt(0).toUpperCase() + area.slice(1)}</b> City Center`);

    // Safe places markers
    const typeColors = { police: "#3b82f6", hospital: "#22c55e", station: "#f59e0b" };
    areaData.safePlaces.forEach(p => {
      const icon = window.L.divIcon({ html: `<div class="safe-pin" style="background:${typeColors[p.type]||"#6b7280"}"></div>`, iconSize: [14, 14], iconAnchor: [7, 7], className: "" });
      window.L.marker([p.lat, p.lng], { icon }).addTo(mapRef.current).bindPopup(`<b>${p.name}</b>`);
    });

    // User location if available
    if (userLocation) {
      const uIcon = window.L.divIcon({ html: `<div class="my-pin"></div>`, iconSize: [22, 22], iconAnchor: [11, 11], className: "" });
      window.L.marker([userLocation.latitude, userLocation.longitude], { icon: uIcon }).addTo(mapRef.current).bindPopup("<b>📍 You are here</b>");
    }

    return () => { if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; } };
  }, [area, areaData, userLocation, mapId]);
  return <div id={mapId} className="leaflet-map area-leaflet-map" />;
}

// ── Main App ──────────────────────────────────────────────────────────────────
export default function Home() {
  const [activeTab,       setActiveTab]       = useState("home");
  const [notification,    setNotification]    = useState(null);
  const [currentQuote,    setCurrentQuote]    = useState(QUOTES[0]);
  const [batteryLevel,    setBatteryLevel]    = useState(null);
  // Welcome screen
  const [showWelcome,     setShowWelcome]     = useState(() => !LS.get("sh_welcomed", false));
  // Online/offline
  const [isOnline,        setIsOnline]        = useState(navigator.onLine);

  const [sosActive,       setSosActive]       = useState(false);
  const [language,        setLanguage]        = useState(() => LS.get("sh_lang", "en"));
  const [onboarded,       setOnboarded]       = useState(() => LS.get("sh_onboarded", false));
  const [showOnboard,     setShowOnboard]     = useState(false);
  const [showPrivacy,     setShowPrivacy]     = useState(false);
  const [reachedSafely,   setReachedSafely]   = useState(false);
  const [voiceActive,     setVoiceActive]     = useState(false);
  const [fallDetected,    setFallDetected]    = useState(false);
  const [sosCountdown,    setSosCountdown]    = useState(null);
  const [sosModal,        setSosModal]        = useState(false);
  const [sosMessage,      setSosMessage]      = useState("");
  const audioRef = useRef(null);

  const [location,        setLocation]        = useState(() => {
    // Auto-set Dharapuram as default so distances show immediately
    const saved = LS.get("sh_lastcity", null);
    if (saved) return saved;
    return { latitude: 10.7322, longitude: 77.5144, accuracy: 0, manual: true, cityName: "Dharapuram" };
  });
  const [locationError,   setLocationError]   = useState(null);
  const [isTracking,      setIsTracking]      = useState(false);
  const [watchId,         setWatchId]         = useState(null);
  const [showMap,         setShowMap]         = useState(false);
  const [manualCity,      setManualCity]      = useState("Dharapuram");
  const [locationMode,    setLocationMode]    = useState("manual"); // "gps" | "manual"

  const [contacts,        setContacts]        = useState(() => LS.get("sh_contacts", []));
  const [cName,           setCName]           = useState("");
  const [cPhone,          setCPhone]          = useState("");
  const [cRelation,       setCRelation]       = useState("Family");

  const [recordings,      setRecordings]      = useState([]);
  const [uploading,       setUploading]       = useState({}); // { recId: true/false }
  const [uploadUrls,      setUploadUrls]      = useState(() => LS.get("sh_uploadurls", {})); // { recId: cloudUrl }
  const [recording,       setRecording]       = useState(false);
  const mrRef = useRef(null); const chunksRef = useRef([]);

  const [fakeActive,      setFakeActive]      = useState(false);
  const [fakeCaller,      setFakeCaller]      = useState("");
  const [fakeCountdown,   setFakeCountdown]   = useState(null); // countdown 3,2,1
  const fakeTimerRef = useRef(null);

  const [areaInput,       setAreaInput]       = useState("");
  const [areaResult,      setAreaResult]      = useState(null);
  const [selArea,         setSelArea]         = useState("");
  const [areaReport,      setAreaReport]      = useState("");
  const [allReports,      setAllReports]      = useState(() => LS.get("sh_reports", {}));
  const [areaDistances,   setAreaDistances]   = useState({});

  useEffect(() => { LS.set("sh_contacts", contacts); }, [contacts]);
  useEffect(() => { if (location) LS.set("sh_lastcity", location); }, [location]);
  useEffect(() => { LS.set("sh_uploadurls", uploadUrls); }, [uploadUrls]);

  // Load recordings from IndexedDB on startup
  useEffect(() => {
    getAllRecordingsFromDB().then(saved => {
      if (saved.length > 0) {
        // Restore blob URLs from stored base64
        const restored = saved.map(r => ({
          ...r,
          url: r.blobBase64
            ? URL.createObjectURL(base64ToBlob(r.blobBase64, "audio/webm"))
            : null,
        })).filter(r => r.url);
        setRecordings(restored);
      }
    }).catch(() => {});
  }, []);
  useEffect(() => { LS.set("sh_reports",  allReports); }, [allReports]);
  useEffect(() => { setCurrentQuote(QUOTES[new Date().getDate() % QUOTES.length]); }, []);

  // ── Online/offline detection + auto-flush queued SOS messages ──
  useEffect(() => {
    const goOnline = () => {
      setIsOnline(true);
      const flushed = flushSOSQueue();
      if (flushed > 0) showNotif(`📤 Back online! Sent ${flushed} queued SOS message(s).`, "info");
      else showNotif("🟢 Back online!", "info");
    };
    const goOffline = () => {
      setIsOnline(false);
      showNotif("🔴 You are offline — SOS will be queued and sent when signal returns", "warning");
    };
    window.addEventListener("online",  goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      window.removeEventListener("online",  goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);

  useEffect(() => {
    if ("getBattery" in navigator) navigator.getBattery().then(b => { setBatteryLevel(Math.round(b.level * 100)); b.addEventListener("levelchange", () => setBatteryLevel(Math.round(b.level * 100))); });
  }, []);

  // SOS countdown
  useEffect(() => {
    if (sosCountdown === null) return;
    if (sosCountdown === 0) { setSosActive(true); setSosCountdown(null); triggerSOSAlert(); return; }
    const t = setTimeout(() => setSosCountdown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [sosCountdown]); // eslint-disable-line

  // Unlock audio on first tap (mobile browsers block autoplay)
  useEffect(() => {
    const unlock = () => {
      if (audioRef.current) {
        audioRef.current.play().then(() => {
          audioRef.current.pause();
          audioRef.current.currentTime = 0;
        }).catch(() => {});
      }
      document.removeEventListener("touchstart", unlock);
      document.removeEventListener("click", unlock);
    };
    document.addEventListener("touchstart", unlock, { once: true });
    document.addEventListener("click", unlock, { once: true });
  }, []);

  // Siren
  useEffect(() => {
    if (!audioRef.current) return;
    if (sosActive) {
      // Try to play — will work after user has tapped screen once
      audioRef.current.play().catch(() => {
        showNotif("🔊 Tap screen once to enable siren sound");
      });
    } else {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  }, [sosActive]);

  // Shake
  useEffect(() => {
    let lX = null, lY = null, lZ = null, last = 0;
    const h = e => {
      const { x, y, z } = e.accelerationIncludingGravity || {};
      if (!x) return;
      if (lX !== null) {
        const d = Math.abs(x-lX)+Math.abs(y-lY)+Math.abs(z-lZ);
        const now = Date.now();
        if (d > 25 && now-last > 3000) { last=now; showNotif("📳 Shake! Calling first contact...","warning"); if (contacts.length>0) { window.location.href = `tel:${contacts[0].phone}`; } }
      }
      lX=x; lY=y; lZ=z;
    };
    window.addEventListener("devicemotion", h);
    return () => window.removeEventListener("devicemotion", h);
  }, [contacts]);

  // Fake call countdown
  useEffect(() => {
    if (fakeCountdown === null) return;
    if (fakeCountdown === 0) {
      setFakeCountdown(null);
      setFakeActive(true);
      return;
    }
    fakeTimerRef.current = setTimeout(() => setFakeCountdown(c => c - 1), 1000);
    return () => { if (fakeTimerRef.current) clearTimeout(fakeTimerRef.current); };
  }, [fakeCountdown]);

  // ── Shorthand translation helper ──────────────────────────────────────────
  const t = (key) => T[language]?.[key] || T["en"][key] || key;

  // ── Persist language ───────────────────────────────────────────────────────
  useEffect(() => { LS.set("sh_lang", language); }, [language]);

  // ── Show onboarding on first visit ────────────────────────────────────────
  useEffect(() => {
    if (!onboarded) setShowOnboard(true);
  }, [onboarded]);

  // ── Auto-start recording when SOS activates ───────────────────────────────
  useEffect(() => {
    if (sosActive && !recording) {
      startRec().catch(() => {});
    }
  }, [sosActive]); // eslint-disable-line

  // ── Fall Detection — phone dropped suddenly ───────────────────────────────
  useEffect(() => {
    let lastMag = 0, lastFall = 0;
    const handleFall = (e) => {
      const { x, y, z } = e.accelerationIncludingGravity || {};
      if (!x) return;
      const mag = Math.sqrt(x*x + y*y + z*z);
      const now = Date.now();
      // Free-fall = near 0, then sudden spike
      if (lastMag < 3 && mag > 25 && now - lastFall > 5000) {
        lastFall = now;
        showNotif("📱 Fall detected! Are you okay?", "warning");
        setFallDetected(true);
        setTimeout(() => setFallDetected(false), 8000);
      }
      lastMag = mag;
    };
    window.addEventListener("devicemotion", handleFall);
    return () => window.removeEventListener("devicemotion", handleFall);
  }, []); // eslint-disable-line

  // ── Voice Activation — "HELP ME" triggers SOS ────────────────────────────
  useEffect(() => {
    if (!voiceActive) return;
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      showNotif("⚠️ Voice not supported on this browser", "warning");
      setVoiceActive(false);
      return;
    }
    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = false;
    recognition.lang = language === "ta" ? "ta-IN" : "en-IN";
    recognition.onresult = (e) => {
      const heard = e.results[e.results.length - 1][0].transcript.toLowerCase().trim();
      console.log("[SaveHer Voice]", heard);
      if (heard.includes("help") || heard.includes("உதவி") || heard.includes("help me") || heard.includes("save me")) {
        showNotif("🎤 Voice SOS triggered!", "danger");
        activateSOS();
      }
    };
    recognition.onerror = () => { setVoiceActive(false); };
    recognition.start();
    showNotif("🎤 Voice active — say 'HELP ME' to trigger SOS");
    return () => { try { recognition.stop(); } catch {} };
  }, [voiceActive, language]); // eslint-disable-line

  // ── Power button / Volume key detection (keydown) ─────────────────────────
  useEffect(() => {
    let pressCount = 0, pressTimer = null;
    const handleKey = (e) => {
      // Volume keys on Android fire as ArrowUp/ArrowDown or MediaVolume
      if (["ArrowUp","ArrowDown","VolumeUp","VolumeDown","F1","F2"].includes(e.key)) {
        pressCount++;
        if (pressTimer) clearTimeout(pressTimer);
        pressTimer = setTimeout(() => { pressCount = 0; }, 2000);
        if (pressCount >= 3) {
          pressCount = 0;
          showNotif("🚨 Volume button SOS triggered!", "danger");
          activateSOS();
        }
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, []); // eslint-disable-line

  // ── Reached Safely ────────────────────────────────────────────────────────
  const sendReachedSafely = () => {
    const msg = `✅ I have reached safely! No need to worry. — SaveHer App`;
    contacts.forEach(c => { window.location.href = `sms:${c.phone}?body=${encodeURIComponent(msg)}`; });
    setReachedSafely(true);
    showNotif("✅ Safety confirmation sent to all contacts!");
    setTimeout(() => setReachedSafely(false), 5000);
  };

  useLeafletMap("loc-map", showMap ? location : null);

  // ── Real-time distance calculation using Haversine ────────────────────────
  const calcDistances = useCallback((area, userLoc) => {
    if (!TN_AREAS[area]) return;
    // Always use a location — fallback to Dharapuram if nothing set
    const loc = userLoc || { latitude: 10.7322, longitude: 77.5144 };
    const dists = {};
    TN_AREAS[area].safePlaces.forEach(p => {
      dists[p.name] = haversine(loc.latitude, loc.longitude, p.lat, p.lng);
    });
    dists["_city"] = haversine(loc.latitude, loc.longitude, TN_AREAS[area].lat, TN_AREAS[area].lng);
    setAreaDistances(dists);
  }, []);

  const showNotif = (msg, type = "info") => { setNotification({ msg, type }); setTimeout(() => setNotification(null), 4000); };

  const getTimeRisk = () => {
    const h = new Date().getHours();
    if (h>=6&&h<18)  return { label:"Low Risk",    color:"#22c55e", sub:"Daytime — Stay aware",    pct: 25 };
    if (h>=18&&h<21) return { label:"Medium Risk",  color:"#f59e0b", sub:"Evening — Be cautious",  pct: 60 };
    return                  { label:"High Risk",    color:"#ef4444", sub:"Night — Stay alert!",    pct: 90 };
  };
  const timeRisk = getTimeRisk();

  const getRatingColor = r => r>=8?"#22c55e":r>=7?"#84cc16":r>=6?"#f59e0b":r>=5?"#f97316":"#ef4444";
  const typeIcon = t => t==="police"?"🚔":t==="hospital"?"🏥":"🚉";

  // SOS — sends SMS + WhatsApp to all contacts
  const triggerSOSAlert = () => {
    const loc  = location ? `${location.latitude.toFixed(5)}, ${location.longitude.toFixed(5)}` : "Unavailable";
    const link = location ? `https://maps.google.com/?q=${location.latitude},${location.longitude}` : "";
    const msg  = `🚨 EMERGENCY! I need immediate help.\n📍 Location: ${loc}\n${link ? `🗺️ Map: ${link}\n` : ""}⏰ Time: ${new Date().toLocaleString()}\n— SaveHer App`;
    setSosMessage(msg);

    if (contacts.length === 0) {
      showNotif("No contacts! Add emergency contacts first.", "warning");
      setTimeout(() => setSosModal(true), 600);
      return;
    }

    if (navigator.onLine) {
      // ONLINE — send immediately via SMS, WhatsApp, and Call
      contacts.forEach((c, idx) => {
        try { window.location.href = `sms:${c.phone}?body=${encodeURIComponent(msg)}`; } catch {}
        setTimeout(() => {
          try {
            const n = c.phone.replace(/[^0-9]/g, "");
            const n91 = n.startsWith("91") ? n : `91${n}`;
            window.location.href = `https://wa.me/${n91}?text=${encodeURIComponent(msg)}`;
          } catch {}
        }, (idx + 1) * 1200);
        if (idx === 0) {
          setTimeout(() => { try { window.location.href = `tel:${c.phone}`; } catch {} }, 2800);
        }
      });
      showNotif("SOS! SMS + WhatsApp + Call sent!", "danger");
    } else {
      // OFFLINE — queue message, auto-send when internet returns
      queueSOS(msg, contacts);
      showNotif("SOS! Offline — message QUEUED. Auto-sends when signal returns!", "danger");
    }
    setTimeout(() => setSosModal(true), 600);
  };

  const activateSOS = () => {
    if (sosActive) { setSosActive(false); setSosModal(false); showNotif("✅ SOS deactivated"); }
    else { fetchLocation(); setSosCountdown(5); }
  };

  // Contacts
  const addContact = () => {
    if (!cName.trim()||!cPhone.trim()) { showNotif("⚠️ Fill name and phone","warning"); return; }
    const c = { _id: Date.now().toString(), name:cName.trim(), phone:cPhone.trim(), relation:cRelation };
    setContacts([c,...contacts]); setCName(""); setCPhone("");
    showNotif(`✅ ${c.name} added`);
  };
  const delContact = id => { setContacts(contacts.filter(c=>c._id!==id)); showNotif("🗑️ Removed"); };

  // Location
  // Set location from manual city dropdown — recalculates all distances immediately
  const applyManualCity = (cityName) => {
    const coords = DEMO_CITIES[cityName];
    if (!coords) return;
    const l = { ...coords, accuracy: 0, manual: true, cityName };
    setLocation(l);
    setLocationError(null);
    setLocationMode("manual");
    setManualCity(cityName);
    showNotif("📍 Location set to " + cityName);
    // Recalculate distances immediately using new location (don't wait for state)
    if (selArea && TN_AREAS[selArea]) {
      const dists = {};
      TN_AREAS[selArea].safePlaces.forEach(p => {
        dists[p.name] = haversine(l.latitude, l.longitude, p.lat, p.lng);
      });
      dists["_city"] = haversine(l.latitude, l.longitude, TN_AREAS[selArea].lat, TN_AREAS[selArea].lng);
      setAreaDistances(dists);
    }
  };

  const fetchLocation = () => {
    if (!("geolocation" in navigator)) { setLocationError("Not supported"); return; }
    navigator.geolocation.getCurrentPosition(
      pos => {
        const l={latitude:pos.coords.latitude,longitude:pos.coords.longitude,accuracy:pos.coords.accuracy,manual:false};
        setLocation(l); setLocationError(null); setLocationMode("gps");
        showNotif("📍 Real GPS location updated");
        if (selArea) calcDistances(selArea, l);
      },
      err => {
        setLocationError(err.message);
        showNotif("⚠️ GPS failed — use manual city instead","warning");
      }
    );
  };
  const toggleTrack = () => {
    if (isTracking) { navigator.geolocation.clearWatch(watchId); setIsTracking(false); setWatchId(null); showNotif("🔴 Tracking stopped"); }
    else { const id=navigator.geolocation.watchPosition(pos=>{const l={latitude:pos.coords.latitude,longitude:pos.coords.longitude,accuracy:pos.coords.accuracy};setLocation(l);if(selArea)calcDistances(selArea,l);},err=>setLocationError(err.message),{enableHighAccuracy:true,maximumAge:5000}); setWatchId(id); setIsTracking(true); showNotif("🟢 Live tracking started"); }
  };

  // ── Recorder helpers ──────────────────────────────────────────────────────
  const blobToBase64 = (blob) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result.split(",")[1]);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

  const base64ToBlob = (b64, type) => {
    const bytes = atob(b64);
    const arr   = new Uint8Array(bytes.length);
    for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
    return new Blob([arr], { type });
  };

  // Recorder
  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      mrRef.current = mr; chunksRef.current = [];
      mr.ondataavailable = e => chunksRef.current.push(e.data);
      mr.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        const url  = URL.createObjectURL(blob);
        const id   = Date.now();
        const rec  = {
          url,
          id,
          ts:   new Date().toLocaleString(),
          size: (blob.size / 1024).toFixed(1),
          blob, // keep blob for upload
        };
        // Save to state
        setRecordings(p => [rec, ...p]);
        // Save permanently to IndexedDB (as base64)
        try {
          const b64 = await blobToBase64(blob);
          await saveRecordingToDB({ id, ts: rec.ts, size: rec.size, blobBase64: b64 });
          showNotif("💾 Recording saved permanently!");
        } catch {
          showNotif("💾 Recording saved (session only)");
        }
      };
      mr.start(); setRecording(true);
      showNotif("🎤 Recording started — capturing evidence");
    } catch { showNotif("❌ Microphone access denied", "danger"); }
  };

  const stopRec = () => { mrRef.current?.stop(); setRecording(false); };

  // Upload recording to Cloudinary cloud
  const uploadRecording = async (rec) => {
    if (!rec.blob && !rec.url) { showNotif("❌ No audio data to upload", "danger"); return; }
    setUploading(p => ({ ...p, [rec.id]: true }));
    showNotif("☁️ Uploading to cloud...");
    try {
      // Get blob — from memory or reconstruct from IndexedDB base64
      let blob = rec.blob;
      if (!blob) {
        const db_recs = await getAllRecordingsFromDB();
        const dbRec   = db_recs.find(r => r.id === rec.id);
        if (dbRec?.blobBase64) blob = base64ToBlob(dbRec.blobBase64, "audio/webm");
      }
      if (!blob) throw new Error("No audio data");
      const cloudUrl = await uploadToCloudinary(blob, `evidence-${rec.id}.webm`);
      setUploadUrls(p => ({ ...p, [rec.id]: cloudUrl }));
      showNotif("✅ Uploaded to cloud successfully!");
    } catch (err) {
      showNotif("❌ Upload failed — check internet connection", "danger");
      console.error(err);
    } finally {
      setUploading(p => ({ ...p, [rec.id]: false }));
    }
  };

  // Delete recording from state + IndexedDB
  const deleteRec = async (id) => {
    setRecordings(p => p.filter(r => r.id !== id));
    setUploadUrls(p => { const n={...p}; delete n[id]; return n; });
    try { await deleteRecordingFromDB(id); } catch {}
    showNotif("🗑️ Recording deleted");
  };

  // Fake call
  const triggerFake = () => {
    // If already active or counting, cancel it
    if (fakeActive) { cancelFakeCall(); return; }
    const c = FAKE_CALLERS[Math.floor(Math.random()*FAKE_CALLERS.length)];
    setFakeCaller(c);
    setFakeCountdown(3);
    showNotif(`⏱️ Fake call from "${c}" in 3 seconds... tap again to cancel`);
  };

  const cancelFakeCall = () => {
    if (fakeTimerRef.current) { clearTimeout(fakeTimerRef.current); fakeTimerRef.current = null; }
    setFakeActive(false);
    setFakeCountdown(null);
    showNotif("📵 Fake call cancelled");
  };

  const endFakeCall = () => {
    setFakeActive(false);
    setFakeCountdown(null);
    showNotif("📵 Call ended");
  };

  // Safety
  const searchArea = key => {
    const k = (key||areaInput).trim().toLowerCase();
    if (!k) return;
    setSelArea(k);
    const data = TN_AREAS[k];
    if (data) {
      setAreaResult(data);
      // Get the most current location — state or fallback to Dharapuram
      const loc = location || { latitude: 10.7322, longitude: 77.5144, manual: true, cityName: "Dharapuram" };
      // Calculate immediately with current location value
      const dists = {};
      data.safePlaces.forEach(p => {
        dists[p.name] = haversine(loc.latitude, loc.longitude, p.lat, p.lng);
      });
      dists["_city"] = haversine(loc.latitude, loc.longitude, data.lat, data.lng);
      setAreaDistances(dists);
      const cityLabel = loc.cityName || `${loc.latitude.toFixed(3)}, ${loc.longitude.toFixed(3)}`;
      showNotif(`✅ Distances calculated from ${cityLabel}`);
    }
    else showNotif("⚠️ City not found. Try another name.", "warning");
  };

  const submitReport = () => {
    if (!selArea||!areaReport.trim()) { showNotif("⚠️ Select area and write report","warning"); return; }
    const e = {text:areaReport.trim(),time:new Date().toLocaleString(),id:Date.now()};
    setAllReports(p=>({...p,[selArea]:[e,...(p[selArea]||[])]}));
    setAreaReport(""); showNotif("📝 Report saved!");
  };
  const delReport = (area,id) => setAllReports(p=>({...p,[area]:(p[area]||[]).filter(r=>r.id!==id)}));
  const areaReports = allReports[selArea]||[];

  // ── RENDER ────────────────────────────────────────────────────────────────

  // ── RENDER ────────────────────────────────────────────────────────────────
  return (
    <div className="app">

      {/* ── Welcome / Splash Screen ── */}
      {showWelcome && (
        <div className="welcome-overlay">
          <div className="welcome-content">
            <div className="welcome-logo-wrap">
              <img src={process.env.PUBLIC_URL + "/logo.png"} alt="SaveHer" className="welcome-logo"
                onError={e=>e.target.style.display="none"}/>
              <div className="welcome-logo-ring"/>
              <div className="welcome-logo-ring r2"/>
            </div>
            <h1 className="welcome-title">SaveHer</h1>
            <p className="welcome-tagline">Your Safety. Your Power. Always.</p>
            <div className="welcome-features">
              <div className="wf-item"><span>🚨</span><span>One-tap SOS Alert</span></div>
              <div className="wf-item"><span>📍</span><span>Real-time Location Share</span></div>
              <div className="wf-item"><span>🛡️</span><span>Tamil Nadu Safety Data</span></div>
              <div className="wf-item"><span>📴</span><span>Works 100% Offline</span></div>
            </div>
            <button className="welcome-btn" onClick={()=>{ LS.set("sh_welcomed", true); setShowWelcome(false); }}>
              Get Started →
            </button>
            <p className="welcome-note">Your data stays on your device only. No server. No tracking.</p>
          </div>
        </div>
      )}

      {/* Toast */}
      {notification && <div className={`toast t-${notification.type}`}>{notification.msg}</div>}

      {/* Online/Offline banner */}
      {!isOnline && (
        <div className="offline-banner">
          🔴 Offline Mode — SOS messages will be sent automatically when signal returns
        </div>
      )}

      {/* ── SOS Modal ── */}
      {sosModal && (
        <div className="modal-overlay">
          <div className="sos-modal">
            <div className="sos-modal-top">
              <span className="sos-modal-icon">🚨</span>
              <h2>SOS Alert Activated</h2>
              <p>SMS + WhatsApp sent to all contacts. Use buttons below if needed:</p>
            </div>
            <div className="msg-box">
              <p className="box-label">📋 SOS Message</p>
              <pre className="msg-pre">{sosMessage}</pre>
              <button className="copy-btn" onClick={()=>{ navigator.clipboard?.writeText(sosMessage).then(()=>showNotif("📋 Copied!")); }}>Copy Message</button>
            </div>
            {contacts.length>0 ? (
              <div className="modal-contacts">
                <p className="box-label">📞 Contact Directly</p>
                {contacts.map(c=>(
                  <div key={c._id} className="modal-contact-row">
                    <div className="mc-info">
                      <span className="mc-name">{c.name}</span>
                      <span className="mc-phone">{c.phone}</span>
                    </div>
                    <div className="mc-btns">
                      <button className="mc-call" onClick={()=>window.location.href=`tel:${c.phone}`}>📞 Call</button>
                      <button className="mc-sms"  onClick={()=>window.location.href=`sms:${c.phone}?body=${encodeURIComponent(sosMessage)}`}>💬 SMS</button>
                      <button className="mc-wa"   onClick={()=>{ const n=c.phone.replace(/[^0-9]/g,""); window.location.href=`https://wa.me/${n.startsWith("91")?n:"91"+n}?text=${encodeURIComponent(sosMessage)}`; }}>🟢 WA</button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="modal-warn">
                <p>⚠️ No contacts saved! Add them in Contacts tab.</p>
                <button className="copy-btn" onClick={()=>{setSosModal(false);setActiveTab("contacts");}}>Add Contacts</button>
              </div>
            )}
            <button className="modal-close" onClick={()=>setSosModal(false)}>Close</button>
          </div>
        </div>
      )}

      {/* ── Fake Call Countdown Bar ── */}
      {fakeCountdown !== null && !fakeActive && (
        <div className="fake-countdown-bar">
          <span>📲 Incoming call in <strong>{fakeCountdown}s</strong> from <strong>{fakeCaller}</strong></span>
          <button className="fake-cancel-small" onClick={cancelFakeCall}>Cancel</button>
        </div>
      )}

      {/* ── Fake Call Screen ── */}
      {fakeActive && (
        <div style={{
          position:"fixed",inset:0,zIndex:99999,
          background:"#0d0810",
          display:"flex",flexDirection:"column",
          alignItems:"center",justifyContent:"center",
          fontFamily:"inherit"
        }}>
          <p style={{fontSize:12,color:"#7a5570",letterSpacing:2,textTransform:"uppercase",marginBottom:24}}>
            Incoming Call
          </p>
          <div style={{
            width:100,height:100,borderRadius:"50%",
            background:"linear-gradient(135deg,#e11d48,#9f1239)",
            display:"flex",alignItems:"center",justifyContent:"center",
            fontSize:40,fontWeight:700,color:"white",
            marginBottom:20,boxShadow:"0 0 32px rgba(225,29,72,0.5)"
          }}>
            {fakeCaller[0]}
          </div>
          <p style={{fontSize:28,fontWeight:700,color:"#f2eaf0",marginBottom:6}}>{fakeCaller}</p>
          <p style={{fontSize:14,color:"#7a5570",marginBottom:60}}>Mobile · SaveHer App</p>
          <div style={{display:"flex",gap:60}}>
            <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:10}}>
              <button
                style={{width:70,height:70,borderRadius:"50%",background:"#ef4444",border:"none",fontSize:26,cursor:"pointer"}}
                onClick={()=>{ setFakeActive(false); showNotif("📵 Call declined"); }}
              >📵</button>
              <span style={{fontSize:13,color:"#c0a0b2"}}>Decline</span>
            </div>
            <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:10}}>
              <button
                style={{width:70,height:70,borderRadius:"50%",background:"#22c55e",border:"none",fontSize:26,cursor:"pointer"}}
                onClick={()=>{ setFakeActive(false); showNotif("📞 Call accepted"); }}
              >📞</button>
              <span style={{fontSize:13,color:"#c0a0b2"}}>Accept</span>
            </div>
          </div>
        </div>
      )}

      {/* ── Onboarding Screen ── */}
      {showOnboard && (
        <div className="onboard-overlay">
          <div className="onboard-card">
            <img src={process.env.PUBLIC_URL + "/logo.png"} alt="SaveHer" className="onboard-logo" onError={e=>e.target.style.display="none"}/>
            <h1 className="onboard-title">{t("onboardTitle")}</h1>
            <p className="onboard-desc">{t("onboardDesc")}</p>
            <div className="onboard-features">
              {[["🚨","SOS Alert"],["📍","Live Location"],["🎤","Evidence Recorder"],["🛡️","Area Safety"],["📲","Fake Call"],["📵","Shake to Call"]].map(([ic,lb])=>(
                <div key={lb} className="onboard-feat"><span>{ic}</span><span>{lb}</span></div>
              ))}
            </div>
            <div className="onboard-lang">
              <button className={`lang-btn ${language==="en"?"lang-on":""}`} onClick={()=>setLanguage("en")}>English</button>
              <button className={`lang-btn ${language==="ta"?"lang-on":""}`} onClick={()=>setLanguage("ta")}>தமிழ்</button>
            </div>
            <button className="onboard-start" onClick={()=>{ setShowOnboard(false); setOnboarded(true); LS.set("sh_onboarded",true); }}>
              {t("getStarted")}
            </button>
            <button className="onboard-privacy" onClick={()=>setShowPrivacy(true)}>🔐 {t("privacyPolicy")}</button>
          </div>
        </div>
      )}

      {/* ── Privacy Policy Modal ── */}
      {showPrivacy && (
        <div className="modal-overlay" onClick={()=>setShowPrivacy(false)}>
          <div className="privacy-card" onClick={e=>e.stopPropagation()}>
            <h2 className="privacy-title">🔐 Privacy Policy</h2>
            <div className="privacy-body">
              <p className="privacy-item">✅ <strong>No data sent to servers</strong> — Everything stored locally on your device only</p>
              <p className="privacy-item">✅ <strong>No account required</strong> — Use anonymously</p>
              <p className="privacy-item">✅ <strong>Location privacy</strong> — GPS used only when you activate SOS or request location</p>
              <p className="privacy-item">✅ <strong>Audio recordings</strong> — Saved only on your device. Cloud upload is optional and only when you tap Upload</p>
              <p className="privacy-item">✅ <strong>Contacts</strong> — Stored in your browser's localStorage. Never shared with anyone</p>
              <p className="privacy-item">✅ <strong>No ads, no tracking</strong> — SaveHer is a free safety tool with zero ads</p>
              <p className="privacy-item">✅ <strong>Open source</strong> — Built with React.js, available on GitHub</p>
            </div>
            <button className="modal-close" onClick={()=>setShowPrivacy(false)}>{t("close")}</button>
          </div>
        </div>
      )}

      {/* ── Fall Detection Alert ── */}
      {fallDetected && (
        <div className="fall-alert">
          <span>📱 Fall detected! Are you okay?</span>
          <div className="fall-btns">
            <button className="fall-ok"  onClick={()=>setFallDetected(false)}>I'm OK ✅</button>
            <button className="fall-sos" onClick={()=>{ setFallDetected(false); activateSOS(); }}>SOS 🚨</button>
          </div>
        </div>
      )}

      {/* ── Header ── */}
      <header className="hdr">
        <div className="hdr-l">
          <img src={process.env.PUBLIC_URL + "/logo.png"} alt="SaveHer" className="hdr-logo" onError={e=>{e.target.style.display="none";}}/>
          <div className="hdr-brand">
            <span className="hdr-title">SaveHer</span>
            <span className="hdr-sub">Women Safety</span>
          </div>
        </div>
        <div className="hdr-r">
          {batteryLevel!==null && <span className={`bat ${batteryLevel<20?"bat-low":""}`}>🔋{batteryLevel}%</span>}
          <button className="lang-toggle" onClick={()=>setLanguage(l=>l==="en"?"ta":"en")}>
            {language==="en"?"தமிழ்":"English"}
          </button>
          <div className="risk-pill" style={{background:timeRisk.color+"18",color:timeRisk.color,borderColor:timeRisk.color+"40"}}>
            <span className="risk-dot" style={{background:timeRisk.color}}/>
            {timeRisk.label}
          </div>
        </div>
      </header>

      {/* ── SOS Zone ── */}
      <div className="sos-section">
        <div className="sos-bg-ring r1"/><div className="sos-bg-ring r2"/><div className="sos-bg-ring r3"/>
        {sosCountdown!==null ? (
          <div className="countdown-wrap">
            <svg className="countdown-svg" viewBox="0 0 100 100">
              <circle cx="50" cy="50" r="44" fill="none" stroke="rgba(225,29,72,0.15)" strokeWidth="6"/>
              <circle cx="50" cy="50" r="44" fill="none" stroke="#e11d48" strokeWidth="6" strokeLinecap="round"
                strokeDasharray={`${(sosCountdown/5)*276.5} 276.5`} strokeDashoffset="69.1"
                style={{transition:"stroke-dasharray 1s linear"}}/>
            </svg>
            <span className="countdown-num">{sosCountdown}</span>
            <p className="countdown-lbl">Activating SOS…</p>
            <button className="cancel-sos" onClick={()=>setSosCountdown(null)}>Cancel</button>
          </div>
        ) : (
          <>
            <button className={`sos-btn ${sosActive?"sos-on":""}`} onClick={activateSOS}>
              {sosActive && <span className="sos-ring"/>}
              {sosActive && <span className="sos-ring r2"/>}
              <span className="sos-lbl">{sosActive?"STOP":"SOS"}</span>
            </button>
            <p className="sos-tip">{sosActive?"🚨 Active — Contacts Notified":"Tap to activate • Shake to call"}</p>
          </>
        )}
        <div className="sos-extra-btns">
          <button className={voiceActive?"voice-btn voice-on":"voice-btn"} onClick={()=>setVoiceActive(v=>!v)}>
            {voiceActive?"🎤 Voice ON":"🎤 Voice OFF"}
          </button>
          {contacts.length>0 && (
            <button className="safe-btn" onClick={sendReachedSafely}>
              {reachedSafely?"✅ Sent!":"✅ Reached Safely"}
            </button>
          )}
        </div>
        <div className="quote-chip">{currentQuote}</div>
      </div>

      {/* ── Tab Nav ── */}
      <nav className="tabs">
        {[["home","🏠","Home"],["contacts","👥","Contacts"],["location","📍","Location"],["recorder","🎤","Recorder"],["safety","🛡️","Safety"]].map(([t,ic,lb])=>(
          <button key={t} className={`tab ${activeTab===t?"tab-on":""}`} onClick={()=>setActiveTab(t)}>
            <span className="tab-ic">{ic}</span>
            <span className="tab-lb">{lb}</span>
          </button>
        ))}
      </nav>

      {/* ── Content ── */}
      <div className="content">

        {/* ══ HOME ══ */}
        {activeTab==="home" && (
          <div className="pg">
            <p className="pg-title">Quick Actions</p>
            <div className="grid3">
              {[
                [fakeActive||fakeCountdown!==null?"📵":"📲", fakeActive||fakeCountdown!==null?"Cancel Call":"Fake Call", fakeActive||fakeCountdown!==null?"tap to cancel":"3 sec delay", fakeActive||fakeCountdown!==null?cancelFakeCall:triggerFake],
                ["📍","Get Location","GPS fetch",fetchLocation],
                ["🚔","Police","Dial 100",()=>window.location.href="tel:100"],
                ["🆘","Women Help","Dial 1091",()=>window.location.href="tel:1091"],
                ["🚑","Ambulance","Dial 102",()=>window.location.href="tel:102"],
                [recording?"⏹️":"🎙️",recording?"Stop Rec":"Record","Evidence",recording?stopRec:startRec],
              ].map(([ic,lb,sub,fn])=>(
                <button key={lb} className="qbtn" onClick={fn}>
                  <span className="qbtn-ic">{ic}</span>
                  <span className="qbtn-lb">{lb}</span>
                  <span className="qbtn-sub">{sub}</span>
                </button>
              ))}
            </div>

            <p className="pg-title">Emergency Helplines</p>
            <div className="grid3">
              {[["🚔","Police","100"],["👩","Women","1091"],["🚑","Ambulance","102"],["🔥","Fire","101"],["🆘","Disaster","108"],["🏥","Child","1098"]].map(([ic,lb,num])=>(
                <button key={num} className="ebtn" onClick={()=>window.location.href=`tel:${num}`}>
                  <span className="ebtn-ic">{ic}</span>
                  <span className="ebtn-lb">{lb}</span>
                  <span className="ebtn-num">{num}</span>
                </button>
              ))}
            </div>

            <div className="stat-row">
              <div className="stat-box">
                <span className="stat-v" style={{color:timeRisk.color}}>{timeRisk.label}</span>
                <span className="stat-l">Current Risk</span>
                <div className="risk-bar">
                  <div className="risk-fill" style={{width:`${timeRisk.pct}%`,background:timeRisk.color}}/>
                </div>
              </div>
              <div className="stat-box">
                <span className="stat-v">{contacts.length}</span>
                <span className="stat-l">Contacts Saved</span>
              </div>
              <div className="stat-box">
                <span className="stat-v">{recordings.length}</span>
                <span className="stat-l">Recordings</span>
              </div>
            </div>
          </div>
        )}

        {/* ══ CONTACTS ══ */}
        {activeTab==="contacts" && (
          <div className="pg">
            <p className="pg-title">Emergency Contacts</p>
            <p className="pg-desc">Saved in browser storage. Auto-notified when SOS activates.</p>
            <div className="form-stack">
              <input className="inp" type="text" placeholder="Full Name"    value={cName}     onChange={e=>setCName(e.target.value)}/>
              <input className="inp" type="tel"  placeholder="Phone Number" value={cPhone}    onChange={e=>setCPhone(e.target.value)}/>
              <select className="inp" value={cRelation} onChange={e=>setCRelation(e.target.value)}>
                <option>Family</option>
                <option>Friend</option>
                <option>Neighbor</option>
                <option>Colleague</option>
              </select>
              <button className="btn-primary" onClick={addContact}>+ Add Contact</button>
            </div>
            {contacts.length===0
              ? <div className="empty"><span>👥</span><p>No contacts yet</p><small>Add people who will be alerted during SOS</small></div>
              : contacts.map(c=>(
                <div key={c._id} className="c-card">
                  <div className="c-av">{c.name[0].toUpperCase()}</div>
                  <div className="c-info">
                    <p className="c-name">{c.name}</p>
                    <p className="c-meta">{c.phone} · <span className="c-rel">{c.relation}</span></p>
                  </div>
                  <div className="c-acts">
                    <button className="ic-btn" onClick={()=>window.location.href=`tel:${c.phone}`}>📞</button>
                    <button className="ic-btn" onClick={()=>window.location.href=`sms:${c.phone}`}>💬</button>
                    <button className="ic-btn del" onClick={()=>delContact(c._id)}>🗑️</button>
                  </div>
                </div>
              ))
            }
          </div>
        )}

        {/* ══ LOCATION ══ */}
        {activeTab==="location" && (
          <div className="pg">
            <p className="pg-title">Location Tracking</p>
            <div className="loc-mode-box">
              <p className="loc-mode-title">📍 Set Your Location</p>
              <div className="loc-mode-tabs">
                <button className={`loc-mode-btn ${locationMode==="manual"?"loc-mode-on":""}`} onClick={()=>setLocationMode("manual")}>🏙️ Select City</button>
                <button className={`loc-mode-btn ${locationMode==="gps"?"loc-mode-on":""}`}    onClick={()=>{fetchLocation();setShowMap(true);}}>📡 Use Device GPS</button>
              </div>
              {locationMode==="manual" && (
                <div className="manual-loc-wrap">
                  <p className="manual-loc-label">Choose your city:</p>
                  <div className="manual-loc-row">
                    <select className="inp" value={manualCity} onChange={e=>{setManualCity(e.target.value);applyManualCity(e.target.value);}}>
                      {Object.keys(DEMO_CITIES).sort().map(c=>(
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                    <button className="btn-primary set-loc-btn" onClick={()=>applyManualCity(manualCity)}>Set</button>
                  </div>
                  <p className="manual-loc-hint">💡 Perfect for laptop demo</p>
                </div>
              )}
              {locationMode==="gps" && (
                <button className={`btn-primary ${isTracking?"btn-danger":"btn-success"}`} style={{marginTop:8}} onClick={toggleTrack}>
                  {isTracking?"🔴 Stop Live Tracking":"🟢 Start Live Tracking"}
                </button>
              )}
            </div>
            {location && location.manual && <div className="loc-manual-badge">🏙️ Location set to <strong>{location.cityName}</strong></div>}
            {location && !location.manual && <div className="gps-ok">✅ Real GPS — {location.latitude.toFixed(5)}, {location.longitude.toFixed(5)}</div>}
            {isTracking && <div className="live-badge"><span className="live-dot"/>Live tracking active</div>}
            {location && (
              <div>
                <div className="map-toolbar">
                  <button className={`map-tog ${showMap?"map-tog-on":""}`} onClick={()=>setShowMap(v=>!v)}>{showMap?"🗺️ Hide Map":"🗺️ Show Map"}</button>
                  <span className="map-note">Offline map (view once online to cache tiles)</span>
                </div>
                {showMap && (
                  <div className="map-wrap">
                    <div id="loc-map" className="leaflet-map"/>
                    <div className="map-coords">📍 {location.latitude.toFixed(5)}, {location.longitude.toFixed(5)}</div>
                  </div>
                )}
                <div className="loc-card" style={{marginTop:8}}>
                  <div className="loc-row"><span className="loc-l">Latitude</span><span className="loc-v">{location.latitude.toFixed(6)}</span></div>
                  <div className="loc-row"><span className="loc-l">Longitude</span><span className="loc-v">{location.longitude.toFixed(6)}</span></div>
                  <div className="loc-row"><span className="loc-l">Source</span><span className="loc-v" style={{fontSize:11}}>{location.manual?"Manual — "+location.cityName:"Real GPS"}</span></div>
                  <div className="loc-acts">
                    <a href={`https://maps.google.com/?q=${location.latitude},${location.longitude}`} target="_blank" rel="noopener noreferrer" className="loc-link">🌐 Google Maps</a>
                    <button className="loc-link" onClick={()=>{ navigator.clipboard?.writeText(`https://maps.google.com/?q=${location.latitude},${location.longitude}`).then(()=>showNotif("📋 Copied!")); }}>📋 Copy</button>
                  </div>
                  {contacts.length>0 && (
                    <button className="share-btn" onClick={()=>{ const m=`My location: https://maps.google.com/?q=${location.latitude},${location.longitude}`; contacts.forEach(c=>window.location.href=`sms:${c.phone}?body=${encodeURIComponent(m)}`); showNotif("📤 Shared!"); }}>
                      📤 Share with All Contacts
                    </button>
                  )}
                </div>
              </div>
            )}
            {locationError && <p className="err-msg">⚠️ {locationError}</p>}
          </div>
        )}

        {/* ══ RECORDER ══ */}
        {activeTab==="recorder" && (
          <div className="pg">
            <p className="pg-title">Evidence Recorder</p>
            <div className="rec-features">
              <span className="rec-feat">💾 Saved Permanently</span>
              <span className="rec-feat">☁️ Cloud Upload</span>
              <span className="rec-feat">📱 Survives App Close</span>
            </div>
            <div className="rec-center">
              {!recording
                ? <button className="rec-btn" onClick={startRec}><span className="rec-dot"/>Start Recording</button>
                : <button className="rec-btn rec-on" onClick={stopRec}><span className="rec-dot on"/>Stop Recording</button>
              }
            </div>
            {recording && (
              <div className="rec-live">
                <span className="rec-dot on" style={{width:10,height:10}}/>
                Recording in progress… tap Stop when done
              </div>
            )}
            {recordings.length===0 && !recording && (
              <div className="empty">
                <span>🎤</span>
                <p>No recordings yet</p>
                <small>Recordings are stored permanently in your device</small>
              </div>
            )}
            {recordings.length>0 && (
              <div className="rec-list">
                <p className="pg-title">Saved Recordings ({recordings.length})</p>
                {recordings.map(r=>(
                  <div key={r.id} className="rec-card-full">
                    <div className="rec-card-top">
                      <div className="rec-card-meta">
                        <span className="rec-ts">🎤 {r.ts}</span>
                        <span className="rec-size">{r.size} KB</span>
                      </div>
                      {uploadUrls[r.id] && (
                        <a href={uploadUrls[r.id]} target="_blank" rel="noopener noreferrer" className="cloud-link">
                          ☁️ View in Cloud
                        </a>
                      )}
                    </div>
                    <audio controls src={r.url} style={{width:"100%",margin:"8px 0"}}/>
                    <div className="rec-card-acts">
                      <a href={r.url} download={`evidence-${r.id}.webm`} className="rec-act-btn dl-btn">⬇️ Download</a>
                      {!uploadUrls[r.id] ? (
                        <button className="rec-act-btn upload-btn" onClick={()=>uploadRecording(r)} disabled={uploading[r.id]}>
                          {uploading[r.id]?"⏳ Uploading…":"☁️ Upload to Cloud"}
                        </button>
                      ) : (
                        <button className="rec-act-btn uploaded-btn" disabled>✅ Uploaded</button>
                      )}
                      <button className="rec-act-btn del-btn" onClick={()=>deleteRec(r.id)}>🗑️ Delete</button>
                    </div>
                    {uploadUrls[r.id] && (
                      <div className="cloud-url-box">
                        <p className="cloud-url-label">☁️ Cloud Link (share as evidence):</p>
                        <p className="cloud-url-text">{uploadUrls[r.id]}</p>
                        <button className="copy-btn" onClick={()=>{ navigator.clipboard?.writeText(uploadUrls[r.id]).then(()=>showNotif("📋 Cloud link copied!")); }}>Copy Link</button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
            <div className="cloud-setup-box">
              <p className="cloud-setup-title">☁️ Cloud Upload Setup</p>
              <p className="cloud-setup-desc">
                Sign up free at <strong>cloudinary.com</strong> → Get Cloud Name → Replace <code>saveher2024</code> in Home.js
              </p>
            </div>
          </div>
        )}

        {/* ══ SAFETY ══ */}
        {activeTab==="safety" && (
          <div className="pg">
            <p className="pg-title">Area Safety — Tamil Nadu</p>
            <p className="pg-desc">Real NCRB 2022 crime data · Haversine distance · 15 cities · Offline map</p>

            <div className="city-from-box">
              <span className="city-from-label">📍 Your City:</span>
              <select className="inp city-from-select" value={manualCity}
                onChange={e=>{ setManualCity(e.target.value); applyManualCity(e.target.value); }}>
                {Object.keys(DEMO_CITIES).sort().map(c=>(
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div className="city-from-badge">
              ✅ Distances from <strong>{location?.cityName||manualCity}</strong> — change anytime above
            </div>

            <div className="search-row">
              <input className="inp" placeholder="e.g. madurai, coimbatore…" value={areaInput}
                onChange={e=>setAreaInput(e.target.value)}
                onKeyDown={e=>e.key==="Enter"&&searchArea()}/>
              <button className="btn-primary srch-btn" onClick={()=>searchArea()}>Search</button>
            </div>

            <div className="chips">
              {Object.keys(TN_AREAS).map(a=>(
                <button key={a} className={`chip ${selArea===a?"chip-on":""}`}
                  onClick={()=>{setAreaInput(a);searchArea(a);}}>
                  {a.charAt(0).toUpperCase()+a.slice(1)}
                </button>
              ))}
            </div>

            {areaResult && (
              <div className="safety-card">

                <div className="sc-head">
                  <div>
                    <h3 className="sc-city">{selArea.charAt(0).toUpperCase()+selArea.slice(1)}</h3>
                    <p className="sc-state">Tamil Nadu · NCRB Data {areaResult.ncrb_year}</p>
                    {areaDistances["_city"] && <p className="sc-dist">📍 {areaDistances["_city"]} from your location</p>}
                  </div>
                  <div className="sc-badge" style={{background:getRatingColor(areaResult.rating)+"18",color:getRatingColor(areaResult.rating),borderColor:getRatingColor(areaResult.rating)+"44"}}>
                    <span className="sc-rating">{areaResult.rating}/10</span>
                    <span className="sc-rlabel">{areaResult.displayRating}</span>
                  </div>
                </div>

                <div className="ncrb-row">
                  <div className="ncrb-box">
                    <span className="ncrb-v">{areaResult.total_crimes.toLocaleString()}</span>
                    <span className="ncrb-l">Total Crimes</span>
                    <span className="ncrb-src">NCRB {areaResult.ncrb_year}</span>
                  </div>
                  <div className="ncrb-box highlight">
                    <span className="ncrb-v" style={{color:"#e11d48"}}>{areaResult.crimes_against_women.toLocaleString()}</span>
                    <span className="ncrb-l">Against Women</span>
                    <span className="ncrb-src">NCRB {areaResult.ncrb_year}</span>
                  </div>
                  <div className="ncrb-box">
                    <span className="ncrb-v" style={{color:timeRisk.color,fontSize:12}}>{timeRisk.label}</span>
                    <span className="ncrb-l">Right Now</span>
                    <span className="ncrb-src">{new Date().toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"})}</span>
                  </div>
                </div>

                <div className="trend-section">
                  <p className="sec-label">Crimes Against Women — Monthly Trend {areaResult.ncrb_year}</p>
                  <div className="trend-chart">
                    {areaResult.trend.map((v,i)=>{
                      const max=Math.max(...areaResult.trend);
                      const h=Math.max(10,Math.round((v/max)*90));
                      const isMax=v===max;
                      return (
                        <div key={i} className="t-col">
                          {isMax && <span className="t-peak">▲</span>}
                          <div className="t-bar" style={{height:h,background:isMax?"#e11d48":"rgba(225,29,72,0.45)"}}/>
                          <span className="t-val">{v}</span>
                          <span className="t-mon">{areaResult.trendLabels[i]}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <p className="sec-label">📍 Area Map (Offline — OpenStreetMap)</p>
                <div className="map-wrap">
                  <AreaLeafletMap key={selArea} area={selArea} areaData={areaResult} userLocation={location}/>
                  <div className="map-legend">
                    <span className="leg-item"><span className="leg-dot" style={{background:"#3b82f6"}}/>Police</span>
                    <span className="leg-item"><span className="leg-dot" style={{background:"#22c55e"}}/>Hospital</span>
                    <span className="leg-item"><span className="leg-dot" style={{background:"#f59e0b"}}/>Station</span>
                    <span className="leg-item"><span className="leg-dot" style={{background:"#e11d48"}}/>You</span>
                  </div>
                </div>

                <p className="sec-label">🏥 Nearby Safe Places — Real Distances (Haversine)</p>
                {areaResult.safePlaces.map((p,i)=>(
                  <div key={i} className="sp-row">
                    <div className="sp-icon-wrap">
                      <span className="sp-type-ic">{typeIcon(p.type)}</span>
                    </div>
                    <div className="sp-info">
                      <p className="sp-name">{p.name}</p>
                      <p className="sp-coords">{p.lat.toFixed(4)}, {p.lng.toFixed(4)}</p>
                    </div>
                    <div className="sp-dist-wrap">
                      <span className="sp-dist" style={{color:areaDistances[p.name]?"#e11d48":"#6b7280"}}>
                        {areaDistances[p.name]||"—"}
                      </span>
                      <span className="sp-dist-lbl">from {location?.cityName||"you"}</span>
                    </div>
                  </div>
                ))}

                <p className="sec-label" style={{marginTop:12}}>📢 Report an Incident</p>
                <textarea className="inp" placeholder="Describe what happened…" value={areaReport}
                  onChange={e=>setAreaReport(e.target.value)} rows={3} style={{resize:"vertical",marginBottom:8}}/>
                <button className="btn-primary" onClick={submitReport}>Submit Report</button>

                {areaReports.length>0 && (
                  <div className="reports">
                    <p className="sec-label">Community Reports ({areaReports.length})</p>
                    {areaReports.map(r=>(
                      <div key={r.id} className="r-item">
                        <p className="r-text">{r.text}</p>
                        <div className="r-foot">
                          <span className="r-time">{r.time}</span>
                          <button className="r-del" onClick={()=>delReport(selArea,r.id)}>🗑️</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

              </div>
            )}

          </div>
        )}

      </div>
      <audio ref={audioRef} src={process.env.PUBLIC_URL + "/siren.mp3"} loop/>
    </div>
  );
}